/**
 * Offset-based pagination control matching the server's `Paginated<T>` envelope
 * (`{ total, limit, offset }`). Emits the new `offset` via `onChange`.
 */
import type { ReactElement } from 'react';
import Button from '@/components/Forms/Button';

export interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
  className?: string;
}

/** Prev/next pager with a "showing X–Y of Z" summary. */
export default function Pagination({
  total,
  limit,
  offset,
  onChange,
  className = '',
}: PaginationProps): ReactElement | null {
  if (total <= 0 || limit <= 0) return null;

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <nav
      className={`flex items-center justify-between gap-4 py-3 ${className}`.trim()}
      aria-label="Phân trang"
    >
      <p className="text-sm text-gray-600">
        Hiển thị <span className="font-medium">{from}</span>–
        <span className="font-medium">{to}</span> trên{' '}
        <span className="font-medium">{total}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => onChange(Math.max(0, offset - limit))}
          disabled={!canPrev}
          aria-label="Trang trước"
        >
          Trước
        </Button>
        <span className="text-sm text-gray-600" aria-current="page">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="secondary"
          onClick={() => onChange(offset + limit)}
          disabled={!canNext}
          aria-label="Trang sau"
        >
          Sau
        </Button>
      </div>
    </nav>
  );
}
